import { getAllUserDevice } from 'src/@api/login'
import { switchDevice } from 'src/utils/switchDevice'
import { store } from 'src/store'
import { messageInfo } from 'src/components/Message'
import { isSelectableDevice } from 'src/utils/subscriptionStatus'
import { refreshSidebarDevices } from 'src/utils/refreshSidebarDevices'
import Router from 'next/router'

/**
 * 解析 URL search 为键值对象。
 * @param {string} search - 如 ?from_app=1&user_device_id=123
 * @returns {Object|null}
 */
export const parseSearchParams = search => {
  try {
    const query = search.match(/\?([^#]+)/)?.[1]
    if (!query) return null

    const obj = {}
    query.split('&').forEach(pair => {
      const [key, value = ''] = pair.split('=')
      if (!key) return
      obj[decodeURIComponent(key)] = decodeURIComponent(value)
    })

    return obj
  } catch {
    return null
  }
}

/**
 * 按平台类型返回对应 Dashboard 路径。
 * @param {string} platformType
 * @returns {string}
 */
export const getDashboardPathByPlatform = platformType => {
  if (platformType === 'Android-Cloud') return '/android/dashboard'
  if (platformType === 'Android-Local') return '/android/local/dashboard'

  return '/dashboard'
}

const buildDeviceUserPayload = device => ({
  username: device.iCloud_username,
  password: device.iCloud_password,
  temporary: device.temporary,
  expire_time: device.expire_time,
  likeType: device.like_type,
  device_id: device?.id || device?.device_id || '',
  order_id: device?.order_id ?? device?.orderId ?? null,
  subscription_status: device?.subscription_status ?? null
})

const getDeviceOrderId = device =>
  device?.order_id ?? device?.orderId ?? device?.Order?.id ?? device?.order?.id ?? null

const applySelectedDevice = ({ device, devices }) => {
  if (!device) {
    return { ok: false, error: 'not_found' }
  }

  if (!isSelectableDevice(device)) {
    return { ok: false, error: 'lapsed_device_not_selectable' }
  }

  const deviceUser = buildDeviceUserPayload(device)

  localStorage.setItem('allDeviceUser', JSON.stringify(devices))
  localStorage.setItem('currentDevice', JSON.stringify({}))
  localStorage.setItem('Basic', device.iCloud_username ? 1 : 0)

  // 统一更新 localStorage 和切换状态；Redux 选中值保持为 device_id。
  const switched = switchDevice({
    dispatch: store.dispatch,
    deviceUser,
    currentDevice: {},
    currentMode: device.platform_type
  })

  if (!switched) {
    return { ok: false, error: 'lapsed_device_not_selectable' }
  }

  return {
    ok: true,
    platformType: device.platform_type,
    dashboardPath: getDashboardPathByPlatform(device.platform_type)
  }
}

/**
 * 按 user_device_id 选中设备并走 switchDevice。
 * @param {string|number} userDeviceId
 * @returns {Promise<{ ok: boolean, platformType?: string, dashboardPath?: string, error?: string }>}
 */
export const applyUserDeviceById = async userDeviceId => {
  const res = await getAllUserDevice({})
  const devices = Array.isArray(res?.data) ? res.data : []
  const device = devices.find(item => String(item.id) === String(userDeviceId))

  return applySelectedDevice({ device, devices })
}

/**
 * 绑定成功后按 orderId 选中刚写入的设备。
 *
 * 为何先等再重试：
 * - 绑定接口返回 SUCCESS 时，设备记录可能尚未出现在 getAllUserDevice 里。
 * - initialDelay 后再拉，失败则按 retryDelay 重试 attempts 次。
 *
 * 为何 finally 里一定 refreshSidebarDevices：
 * - 绑定瞬间的 refresh 往往太早，侧栏 notBindOrder 仍是「Package / Connect the device now」。
 * - 移动端抽屉 keepMounted，不 remount，不靠重进页面刷新。
 * - 等设备写入窗口过后再刷一次，才能清掉未绑 Package、展示已绑设备。
 * - 选中成败都刷：即使没选上，侧栏列表也要跟后端对齐。
 */
export const applyBoundDeviceByOrderId = async ({
  orderId,
  platformType,
  attempts = 3,
  retryDelay = 800,
  initialDelay = 800
} = {}) => {
  if (orderId == null || orderId === '') {
    return { ok: false, error: 'missing_order_id' }
  }

  // 给后端一点写入时间，避免一绑完立刻拉列表为空
  if (initialDelay > 0) {
    await new Promise(resolve => setTimeout(resolve, initialDelay))
  }

  let result = { ok: false, error: 'bound_device_not_found' }
  const totalAttempts = Math.max(1, Number(attempts) || 1)

  try {
    for (let attempt = 0; attempt < totalAttempts; attempt += 1) {
      const res = await getAllUserDevice({})
      const devices = Array.isArray(res?.data) ? res.data : []
      const device = devices.find(item => {
        const sameOrder = String(getDeviceOrderId(item)) === String(orderId)
        const samePlatform = !platformType || item?.platform_type === platformType

        return sameOrder && samePlatform
      })

      if (device) {
        // 写入 DeviceUser + Redux 选中，侧栏高亮切到新设备
        result = applySelectedDevice({ device, devices })
        break
      }

      if (attempt < totalAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, retryDelay))
      }
    }
  } finally {
    // 必刷侧栏：重拉设备列表 + 未绑订单，去掉过期的 Package 卡
    await refreshSidebarDevices()
  }

  return result
}

/**
 * App 深链入站：解析 from_app / user_device_id 并切换设备。
 * @param {string} search - window.location.search
 * @returns {Promise<{ handled: boolean, ok?: boolean, error?: string }>}
 */
export const handleAppDeviceBridge = async search => {
  const params = parseSearchParams(search)
  if (!params || params.from_app !== '1') {
    return { handled: false }
  }

  // App 跳转来源平台持久化；入参可能是 iOS/Android，写入前统一小写
  if (params.app_platform) {
    sessionStorage.setItem('app_platform', String(params.app_platform).trim().toLowerCase())
  }

  const userDeviceId = params.user_device_id

  // 绑定/setup 场景只需 orderId，device id 非必须；无 user_device_id 时不提示、不处理设备桥接
  if (!userDeviceId) {
    return { handled: false }
  }

  try {
    const result = await applyUserDeviceById(userDeviceId)
    if (!result.ok) {
      messageInfo('Invalid user_device_id')

      return { handled: true, ok: false, error: result.error }
    }

    const { dashboardPath } = result

    if (window.location.pathname !== dashboardPath) {
      Router.push(dashboardPath)

      return { handled: true, ok: true, ...result }
    }

    if (window.location.search) {
      window.history.replaceState(null, '', dashboardPath)
    }

    return { handled: true, ok: true, ...result }
  } catch {
    messageInfo('Failed to load device')

    return { handled: true, ok: false, error: 'request_failed' }
  }
}
